import ApiService from '@/core/services/api.service';
import JwtService from '@/core/services/jwt.service';
import fileDownload from '@/utils/file-download';
import axios from 'axios';
import EventSource from 'eventsource';

export const ProviderService = {
  /**
   * Get paginated providers
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  getAll(params = {}) {
    return ApiService.query('v1/providers', { params });
  },

  /**
   * Get provider by ID
   * @param {Number} id - Provider ID
   * @returns {Promise}
   */
  getById(id) {
    return ApiService.get(`v1/providers/${id}`);
  },

  /**
   * Create provider
   * @param {Object} data - Provider data
   * @returns {Promise}
   */
  create(data) {
    return ApiService.post('v1/providers', data);
  },

  /**
   * Update provider
   * @param {Number} id - Provider ID
   * @param {Object} data - Provider data
   * @returns {Promise}
   */
  update(id, data) {
    return ApiService.update(`v1/providers/${id}`, data);
  },

  /**
   * Delete provider
   * @param {Number} id - Provider ID
   * @returns {Promise}
   */
  delete(id) {
    return ApiService.delete(`v1/providers/${id}`);
  },

  /**
   * Bulk create providers
   * @param {Array} data - Array of provider data
   * @returns {Promise}
   */
  bulkCreate(data) {
    return ApiService.post('v1/providers/bulk-create', { providers: data });
  },

  /**
   * Bulk update providers
   * @param {Object} data - Update data with mode and providers array
   * @returns {Promise}
   */
  bulkUpdate(data) {
    return ApiService.post('v1/providers/bulk-update', data);
  },

  /**
   * Export providers to Excel
   * @param {Object} params - Filter parameters
   * @returns {Promise}
   */
  exportToExcel(params = {}) {
    const url = `v1/providers/export-excel`;
    return axios
      .get(url, {
        params,
        responseType: 'blob',
      })
      .then((response) => {
        const fileName = fileDownload.getFileName(response);
        fileDownload.createFile(response.data, fileName);
        return { success: true, fileName };
      });
  },

  /**
   * Import providers from Excel - New API endpoint
   * @param {FormData} formData - FormData with file and mode
   * @returns {Promise}
   */
  importFromExcel(formData) {
    return ApiService.post('v1/providers/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Import providers from Excel with mode
   * @param {File} file - Excel file
   * @param {string} mode - Import mode: 'create_new', 'update_existing', or 'create_or_update'
   * @returns {Promise}
   */
  importWithMode(file, mode = 'create_new') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);

    return ApiService.post('v1/suppliers/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Check which supplier codes already exist
   * @param {Array<string>} codes - Array of supplier codes to check
   * @returns {Promise}
   */
  checkExistingCodes(codes) {
    return ApiService.post('v1/providers/check-codes', { codes });
  },

  /**
   * Download Excel template for import - Updated endpoint
   * @returns {Promise}
   */
  downloadTemplate() {
    return axios.get('v1/providers/import/template', {
      responseType: 'blob',
    });
  },

  /**
   * Get import job status
   * @param {String} jobId - Import job ID
   * @returns {Promise}
   */
  getImportStatus(jobId) {
    return ApiService.get(`v1/imports/status/${jobId}/details`);
  },

  /**
   * Subscribe to import status updates via SSE
   * @param {String} jobId - Import job ID
   * @param {Function} onMessage - Callback for messages
   * @param {Function} onError - Callback for errors
   * @returns {EventSource}
   */
  subscribeToImportStatus(jobId, onMessage, onError) {
    const token = JwtService.getToken();
    // const token =
    //   'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJkYXRhIjp7Il9pZCI6MSwidXNlcm5hbWUiOiJyb290X2FkbWluIiwiaXNBY3RpdmUiOjEsImVtcGxveWVlSWQiOjF9LCJpYXQiOjE3NTAwNzU2MjIsImV4cCI6MTc1MDY4MDQyMn0.4eLv5iW1Fyot8smGI0sySZyRh9Pb03UfL4X0sqBxLWo';
    const baseURL =
      process.env.VUE_APP_API_URL_READ || 'http://localhost:3000/api/';

    const eventSourceInitDict = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const eventSource = new EventSource(
      `${baseURL}v1/imports/status/${jobId}`,
      eventSourceInitDict,
    );

    // const eventSource = new EventSource(
    //   `${baseURL}v1/imports/status/${jobId}`,
    //   {
    //     headers: {
    //       Authorization: `Bearer ${token}`,
    //     },
    //   },
    // );

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      onMessage(data);
    };

    eventSource.onerror = (error) => {
      console.error('SSE error:', error);
      eventSource.close();
      if (onError) {
        onError(error);
      }
    };

    return eventSource;
  },
};
